<script lang="ts">
  // Paints a small height field; white = high.
  import { onMount } from 'svelte';
  import { t } from '../../i18n/index.svelte';
  import { base64ToBytes, u16ToBase64, type HeightData } from '../../scene/types';

  let { data, onchange }: { data: HeightData; onchange: (d: HeightData) => void } = $props();

  const N = 128;
  let canvas: HTMLCanvasElement;
  let raise = $state(true);
  let radius = $state(10);
  let field = new Float32Array(N * N);
  let painting = false;

  function load() {
    field = new Float32Array(N * N);
    const b = base64ToBytes(data.samples);
    const src = new Uint16Array(b.buffer, b.byteOffset, b.byteLength / 2);
    if (data.cols >= 2 && data.rows >= 2 && src.length === data.cols * data.rows) {
      for (let y = 0; y < N; y++)
        for (let x = 0; x < N; x++) {
          const sx = Math.min(data.cols - 1, Math.round((x / (N - 1)) * (data.cols - 1)));
          const sy = Math.min(data.rows - 1, Math.round((y / (N - 1)) * (data.rows - 1)));
          field[y * N + x] = src[sy * data.cols + sx] / 65535;
        }
    }
    draw();
  }

  function draw() {
    const ctx = canvas.getContext('2d')!;
    const img = ctx.createImageData(N, N);
    for (let i = 0; i < N * N; i++) {
      const v = Math.round(field[i] * 255);
      img.data.set([v, v, v, 255], i * 4);
    }
    ctx.putImageData(img, 0, 0);
  }

  function dab(e: PointerEvent) {
    const r = canvas.getBoundingClientRect();
    const cx = ((e.clientX - r.left) / r.width) * N;
    const cy = ((e.clientY - r.top) / r.height) * N;
    for (let y = Math.max(0, Math.floor(cy - radius)); y < Math.min(N, cy + radius); y++)
      for (let x = Math.max(0, Math.floor(cx - radius)); x < Math.min(N, cx + radius); x++) {
        const d = Math.hypot(x - cx, y - cy) / radius;
        if (d >= 1) continue;
        const w = (0.5 + 0.5 * Math.cos(Math.PI * d)) * 0.08;
        const i = y * N + x;
        field[i] = Math.min(1, Math.max(0, field[i] + (raise ? w : -w)));
      }
    draw();
  }

  function commit() {
    const s = new Uint16Array(N * N);
    for (let i = 0; i < s.length; i++) s[i] = Math.round(field[i] * 65535);
    onchange({ cols: N, rows: N, samples: u16ToBase64(s) });
  }

  async function fromImage(f: File) {
    const bmp = await createImageBitmap(f, { resizeWidth: N, resizeHeight: N, resizeQuality: 'high' });
    const c = new OffscreenCanvas(N, N);
    const ctx = c.getContext('2d')!;
    ctx.drawImage(bmp, 0, 0);
    const px = ctx.getImageData(0, 0, N, N).data;
    for (let i = 0; i < N * N; i++)
      field[i] = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
    draw();
    commit();
  }

  onMount(load);
</script>

<div class="painter">
  <canvas
    bind:this={canvas}
    width={N}
    height={N}
    onpointerdown={(e) => {
      painting = true;
      canvas.setPointerCapture(e.pointerId);
      dab(e);
    }}
    onpointermove={(e) => painting && dab(e)}
    onpointerup={() => {
      if (painting) commit();
      painting = false;
    }}
  ></canvas>
  <div class="row tools">
    <div class="seg">
      <button class:on={raise} onclick={() => (raise = true)}>{t('brush.raise')}</button>
      <button class:on={!raise} onclick={() => (raise = false)}>{t('brush.lower')}</button>
    </div>
    <input type="range" min="2" max="40" bind:value={radius} title={t('brush.radius')} />
  </div>
  <label class="file">
    {t('scene.heightFromImage')}
    <input
      type="file"
      accept="image/*"
      onchange={(e) => {
        const f = (e.currentTarget as HTMLInputElement).files?.[0];
        if (f) fromImage(f);
      }}
    />
  </label>
</div>

<style>
  .painter {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  canvas {
    width: 100%;
    aspect-ratio: 1;
    image-rendering: pixelated;
    border: 1px solid var(--line);
    border-radius: 4px;
    cursor: crosshair;
    touch-action: none;
  }
  .tools input {
    flex: 1;
  }
  .file {
    font-size: 12px;
    color: var(--muted);
  }
  .file input {
    font-size: 11px;
    width: 100%;
  }
</style>
